import { forwardRef, useEffect, useState } from "react";
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Slide,
  Box,
  CircularProgress,
  Stack,
  Chip,
  Paper,
  Divider,
  Grid,
  Button,
  Menu,
  MenuItem,
  Link,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import OrderCard from "./OrderCard";
import request from "../utilities/requests";
import formatDate from "../utilities/formatDate";
import { toast } from "../utilities/SnackBarUtils";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const channelLogos = {
  tnfm: "https://d19w6ranc5yet4.cloudfront.net/tnfm/tnfm-logo.png",
  eventbrite: "https://d19w6ranc5yet4.cloudfront.net/tnfm/eb-logo.png",
};

const refundStatusColor = (status) => {
  switch (status) {
    case "succeeded":
      return "success";
    case "pending":
      return "warning";
    case "failed":
    case "canceled":
      return "error";
    default:
      return "default";
  }
};

const OrderDetailDialog = ({ open, orderId, onClose }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const fetchOrder = async () => {
    if (!orderId) return;
    try {
      setLoading(true);
      const result = await request(
        `ticketing/admin/order?orderId=${orderId}`
      );
      if (result.success) {
        setOrder(result.data);
      } else {
        toast.error(result.error || "Failed to load order");
        setOrder(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && orderId) {
      fetchOrder();
    }
    if (!open) {
      setOrder(null);
    }
  }, [open, orderId]);

  const currency = (order?.currency || "usd").toLowerCase();
  const fmt = (cents = 0) =>
    new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency === "usd" ? "USD" : currency.toUpperCase(),
    }).format((cents || 0) / 100);

  const handleResendReceipt = async () => {
    setMenuAnchor(null);
    if (!order) return;
    const result = await request("orders/sendreceipt", "post", {
      orderId: order.orderId,
    });
    if (result.success) toast.success("Receipt sent!");
    else toast.error(result.error || "Failed to send receipt");
  };

  const netCents = order
    ? Number(order.amountTotalCents || 0) - Number(order.refundedAmountCents || 0)
    : 0;

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: "relative" }} color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, ml: 1 }}>
            <Typography variant="h6" component="div">
              {order
                ? `Order #${order.customerOrderId || order.orderId}`
                : "Order"}
            </Typography>
            {order && (
              <Typography variant="caption" color="text.secondary">
                {order.firstName} {order.lastName} • {order.email}
              </Typography>
            )}
          </Box>
          {order?.source === "tnfm" && (
            <>
              <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                <MenuItem onClick={handleResendReceipt}>
                  Resend receipt
                </MenuItem>
              </Menu>
            </>
          )}
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto", width: "100%" }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && order && (
          <Stack spacing={3}>
            {/* Header / customer summary */}
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h6">
                      {order.firstName} {order.lastName}
                    </Typography>
                    {order.complimentary && (
                      <Chip
                        size="small"
                        label="Complimentary"
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {order.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {order.event?.name} | {formatDate(order.event?.start)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Purchased {formatDate(order.createdAt)}
                  </Typography>
                </Stack>

                <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }} spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      component="img"
                      src={channelLogos[order.source]}
                      alt={`${order.source} logo`}
                      sx={{ height: 18, width: "auto" }}
                    />
                    {order.tnfmStatus && (
                      <Chip
                        size="small"
                        label={order.tnfmStatus}
                        variant="outlined"
                        sx={{ textTransform: "capitalize" }}
                      />
                    )}
                    {!order.tnfmStatus && order.status && (
                      <Chip
                        size="small"
                        label={order.status}
                        variant="outlined"
                        sx={{ textTransform: "capitalize" }}
                      />
                    )}
                  </Stack>
                  {order.receiptUrl && (
                    <Link
                      href={order.receiptUrl}
                      target="_blank"
                      rel="noopener"
                      variant="caption"
                    >
                      View receipt
                    </Link>
                  )}
                </Stack>
              </Stack>
            </Paper>

            {/* Amount breakdown */}
            {order.source === "tnfm" && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Amounts
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Subtotal
                    </Typography>
                    <Typography>{fmt(order.amountSubtotalCents)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Discount
                    </Typography>
                    <Typography>
                      {order.amountDiscountCents
                        ? `−${fmt(order.amountDiscountCents)}`
                        : fmt(0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Tax
                    </Typography>
                    <Typography>{fmt(order.amountTaxCents)}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Fees
                    </Typography>
                    <Typography>{fmt(order.amountFeesCents)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total charged
                    </Typography>
                    <Typography fontWeight={600}>
                      {fmt(order.amountTotalCents)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Refunded
                    </Typography>
                    <Typography color={order.refundedAmountCents ? "error" : "inherit"}>
                      {order.refundedAmountCents
                        ? `−${fmt(order.refundedAmountCents)}`
                        : fmt(0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Net
                    </Typography>
                    <Typography fontWeight={600}>{fmt(netCents)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Items + refund flow */}
            <OrderCard order={order} onRefundSuccess={fetchOrder} />

            {/* Refund history */}
            {Array.isArray(order.refunds) && order.refunds.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Refund history
                </Typography>
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {order.refunds.map((r, idx) => (
                    <Stack
                      key={r.refundId || idx}
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" fontWeight={600}>
                            {fmt(r.amount)}
                          </Typography>
                          <Chip
                            size="small"
                            label={r.status}
                            color={refundStatusColor(r.status)}
                            variant="outlined"
                            sx={{ textTransform: "capitalize" }}
                          />
                        </Stack>
                        {r.reason && (
                          <Typography variant="caption" color="text.secondary">
                            {r.reason}
                          </Typography>
                        )}
                        {r.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {r.notes}
                          </Typography>
                        )}
                      </Stack>
                      <Stack alignItems={{ xs: "flex-start", sm: "flex-end" }}>
                        <Typography variant="caption" color="text.secondary">
                          Requested {formatDate(r.requestedAt)}
                        </Typography>
                        {r.processedAt && (
                          <Typography variant="caption" color="text.secondary">
                            Processed {formatDate(r.processedAt)}
                          </Typography>
                        )}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* Transfers */}
            {Array.isArray(order.transfers) && order.transfers.length > 0 && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Transfers
                </Typography>
                <Stack spacing={1} divider={<Divider flexItem />}>
                  {order.transfers.map((t, idx) => (
                    <Stack key={idx} spacing={0.25}>
                      <Typography variant="body2">
                        {t.fromEventId} → {t.toEventId}
                      </Typography>
                      {t.reason && (
                        <Typography variant="caption" color="text.secondary">
                          {t.reason}
                        </Typography>
                      )}
                      {t.note && (
                        <Typography variant="caption" color="text.secondary">
                          {t.note}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(t.transferredAt)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            )}
          </Stack>
        )}

        {!loading && !order && (
          <Alert severity="error">Unable to load order.</Alert>
        )}
      </Box>
    </Dialog>
  );
};

export default OrderDetailDialog;
